import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
const processSteps = [{
  title: 'Site survey & assessment',
  desc: 'Detailed evaluation of your building, current systems, and thermal loads.'
}, {
  title: 'System design & load calculations',
  desc: 'Bespoke engineering to ensure perfect sizing and optimal efficiency.'
}, {
  title: 'Equipment selection',
  desc: 'Specifying the right units from premium brands like Daikin and Toshiba.'
}, {
  title: 'Professional installation',
  desc: 'F-Gas certified, in-house engineers working to the highest standards.'
}, {
  title: 'Full commissioning & testing',
  desc: 'Rigorous checks to guarantee performance and compliance before handover.'
}, {
  title: 'Handover & documentation',
  desc: 'Comprehensive training, O&M manuals, and warranty registration.'
}];
const sectors = ['Office', 'Retail', 'Hospitality', 'Healthcare', 'Education', 'Industrial'];
export function Installation() {
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-1200">
      {/* Hero */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-1201">
        <div className="container mx-auto px-6" data-id="element-1202">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-1203">
            <div className="lg:col-span-7" data-id="element-1204">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-1205">
                Services / Installation
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark" data-id="element-1206">
                Commercial AC installation designed around your building
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-12" data-id="element-1207">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1208">
                We do not just fit units. We design bespoke systems using proper
                load calculations, manufacturer-trained engineers, and full
                commissioning — so your installation lasts.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Design-led approach */}
      <section className="bg-white py-24" data-id="element-1209">
        <div className="container mx-auto px-6" data-id="element-1210">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1211">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1212">
              <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-1213">
                Why design matters
              </h2>
              <div className="space-y-4 text-primary-dark/70 leading-relaxed" data-id="element-1214">
                <p data-id="element-1215">
                  Most commercial air conditioning failures trace back to poor
                  initial installation. Wrong sizing, inadequate airflow
                  modelling, and skipped commissioning lead to inefficient
                  systems that break down prematurely.
                </p>
                <p data-id="element-1216">
                  At AccuTemp, we start with the building, not the catalogue.
                  Our in-house design team conducts thorough thermal load
                  calculations and space assessments to engineer a system that
                  delivers exact cooling and heating requirements without
                  wasting energy.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-3xl overflow-hidden aspect-[4/3]" data-id="element-1217">
              <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80" alt="Commercial HVAC installation" className="w-full h-full object-cover" data-id="element-1218" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-1219">
        <div className="container mx-auto px-6" data-id="element-1220">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-1221">
            Our installation process
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1222">
            {processSteps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-8 rounded-3xl shadow-sm border border-primary-dark/5 relative overflow-hidden group" data-id="element-1223">
                <div className="text-6xl font-serif text-primary-dark/5 absolute -top-4 -right-2 group-hover:text-accent/10 transition-colors duration-300" data-id="element-1224">
                  0{index + 1}
                </div>
                <h3 className="text-xl font-medium mb-3 relative z-10" data-id="element-1225">
                  {step.title}
                </h3>
                <p className="text-primary-dark/60 text-sm leading-relaxed relative z-10" data-id="element-1226">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="bg-white py-24" data-id="element-1227">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-1228">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-1229">
            Manufacturer partnerships
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed mb-12" data-id="element-1230">
            As a Daikin D1+ Premier Partner, alongside strong relationships with
            Toshiba and Mitsubishi, we offer extended warranties of up to 7
            years. Our engineers receive direct manufacturer training, ensuring
            your system is installed exactly to specification with priority
            access to parts.
          </motion.p>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-1231">
        <div className="container mx-auto px-6" data-id="element-1232">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-1233">
            Sectors we install for
          </motion.h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-1234">
            {sectors.map((sector, index) => <motion.div key={sector} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-white px-8 py-4 rounded-full text-primary-dark font-medium shadow-sm border border-primary-dark/5" data-id="element-1235">
                {sector}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark py-24 text-center" data-id="element-1236">
        <div className="container mx-auto px-6" data-id="element-1237">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-1238">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-1239">
              Ready to discuss your installation?
            </h2>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-1240">
              Get a free site survey
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1241" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}